import { FilterChips } from '@internal/blocks/SearchResultsBlock/FilterChips'
import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext'
import {
  canHaveBaleChamberFilter,
  canHaveHeaderTypeFilter,
  MachineType,
} from '@internal/utils/machine/MachineType'
import { localeNamespace, TLocale } from '@internal/utils/machine/i18n/Locale'
import { LinearProgress, Stack } from '@mui/material'
import { Button } from '@renderer-ui-library/atoms'
import { TFiltersConfiguration } from 'blocks/SearchResultsBlock/TFiltersConfiguration'
import classNames from 'classnames'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { BrandsFilter } from './BrandsFilter'
import { FeaturesFilter } from './FeaturesFilter'
import { LocationFilter } from './LocationFilter'
import { MachineSubTypeFilter } from './MachineSubTypeFilter'
import { MachineTypeFilter } from './MachineTypeFilter'
import { ModelFilter } from './ModelFilter'
import { RadioFilter } from './RadioFilter'
import { RangeFilter } from './RangeFilter'
import styles from './searchFilterBar.module.scss'

type Props =
  | {
      type: 'desktop'
      config: TFiltersConfiguration
      machineType: MachineType | undefined
      onClickApply?: () => void
      onClickReset?: () => void
      locale: TLocale
      isLoading: boolean
    }
  | {
      type: 'mobile'
      config: TFiltersConfiguration
      machineType: MachineType | undefined
      onClickApply: () => void
      onClickReset: () => void
      locale: TLocale
      isLoading: boolean
    }

export const SearchFilterBar: React.FC<Props> = React.memo(
  ({
    machineType,
    config,
    onClickApply,
    onClickReset,
    type,
    locale,
    isLoading,
  }) => {
    const { t } = useTranslation(localeNamespace.common)

    const { searchParams } = useSearchParamsContext()

    return (
      <div
        className={classNames(styles.wrapper, {
          [styles.desktop]: type === 'desktop',
        })}
      >
        <LinearProgress
          color='secondary'
          variant='indeterminate'
          className={styles.progressIndicator}
          style={{ opacity: isLoading ? 1 : 0 }}
        />
        <Stack direction='column' spacing={2}>
          {type === 'mobile' && (
            <FilterChips
              className={styles.mobileChipsWrapper}
              filtersConfiguration={config}
              locale={locale}
              searchParams={searchParams}
            />
          )}
          <MachineTypeFilter />
          <BrandsFilter selectedMachineType={machineType ?? null} />
          <ModelFilter />
          <MachineSubTypeFilter />
          {canHaveBaleChamberFilter(machineType) && <RadioFilter />}
          {canHaveHeaderTypeFilter(machineType) && <RadioFilter />}
          {config.rangeFilters.map((config) => (
            <RangeFilter key={config.machineAttribute} config={config} />
          ))}
          {config.features.length > 0 && (
            <FeaturesFilter options={config.features} />
          )}
          <LocationFilter locale={locale} />
        </Stack>

        {onClickReset && onClickApply && (
          <div className={styles.buttons}>
            <div className={styles.button}>
              <Button
                noMinWidth
                fullWidth
                variant='outlined'
                size='medium'
                onClick={onClickReset}
              >
                {t(translations.searchResultsFilterReset)}
              </Button>
            </div>
            <div className={styles.button}>
              <Button
                color='secondary'
                noMinWidth
                fullWidth
                size='medium'
                onClick={onClickApply}
              >
                {t(translations.searchResultsFilterApply)}
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }
)

SearchFilterBar.displayName = 'SearchFilterBar'
